import type {
  GfPlanPeriod,
  GfProductId,
  GFPurchaseDetails,
} from "../types/wv/common/type";
import {
  determineEnvironment,
  PRODUCTION,
} from "../utils/determineEnvironment";
import { LOCALES_SHORT } from "../utils/types";
import type { OGMessage } from "../utils/wv/constants";

export const INSTAGRAM_URL = "https://www.instagram.com/golffix.official/";
export const YOUTUBE_URL =
  "https://www.youtube.com/channel/UCz5jTSyr5aP07IKq3SD7-gw/featured";
export const NAVER_BLOG_URL = "https://m.blog.naver.com/golffix";
export const NOTION_URL = "https://notice.golffix.io/";

// 웹공유 이벤트 관련 URL 모음
export const WEB_SHARE_EVENT_URL = {
  NAVER_BLOG: "https://m.blog.naver.com/golffix/223110882857",
  NOTION: "https://notice.golffix.io/f43d2ba9-705a-4f3c-ba9a-b8384ea50754",
};

export const LOCALES = {
  EN_US: "en-US",
  KO_KR: "ko-KR",
  JR_JP: "ja-JP",
  FR_FR: "fr-FR",
  ES_ES: "es-ES",
  "ZH-CN": "zh-CN",
  "ZH-TW": "zh-TW",
  DE_DE: "de-DE",
  IT_IT: "it-IT",
  SV_SE: "sv-SE",
};

export const LATIN_LOCALES = [
  LOCALES_SHORT.EN,
  LOCALES_SHORT.FR,
  LOCALES_SHORT.ES,
  LOCALES_SHORT.DE,
  LOCALES_SHORT.IT,
  LOCALES_SHORT.SV,
];

export const TIMEZONE = { ASIA_SEOUL: "Asia/Seoul", UTC: "utc" };

export const UNKNOWN_ERROR = "Unknown error";

export const KR_URL = "https://kr.moais.co.kr/golffix";
export const GLOBAL_URL = "https://www.moais.co.kr/GolfFix";

export const CS_OG_MESSAGES: OGMessage = {
  en: {
    title: "[GolfFix] Help Center",
    description: "Complete a New golf life with GolfFix",
  },
  ko: {
    title: "[GolfFix] [골프픽스] 고객센터",
    description: "골프연습은 골프픽스",
  },
};

// SD-8498 에어브릿지 앱 설치 링크 (다이나링크 대체)
// 스토어 폴백이 유저의 국가/언어에 맞는 스토어로 보내주므로 로케일별 분기 없이 단일 링크를 쓴다.
// 운영만 go, 그 외 환경(테스트베드·개발·로컬)은 테스트베드 링크(tbgo)로 보낸다.
export const AIRBRIDGE_INSTALL_BASE_URL =
  determineEnvironment() === PRODUCTION
    ? "https://go.golffix.io/@golffix"
    : "https://tbgo.golffix.io/@golffix";

// /purchase 페이지 CTA 전용 고정 설치 링크.
// 에어브릿지 숏링크 하나로 채널/캠페인이 이미 정의돼 있어 utm 조합 없이 그대로 사용한다.
export const PURCHASE_CTA_INSTALL_URL = "https://go.golffix.io/4s42gf";

// utm_source는 경로 세그먼트라 누락 시 제외가 불가능해 기본 채널로 대체한다.
export const AIRBRIDGE_DEFAULT_CHANNEL = "web_share";

// 항상 붙는 고정 파라미터 (딥링크 목적지 + 스토어 폴백)
export const AIRBRIDGE_FIXED_PARAMS: ReadonlyArray<readonly [string, string]> =
  [
    ["deeplink_url", "golffix-internal://main?gnb=tab_home"],
    ["fallback_ios", "itunes-appstore"],
    ["fallback_android", "google-play"],
  ];

export const directionMapping: Record<string, 0 | 1> = {
  FRONT: 0,
  SIDE: 1,
};

// 스윙 문제점 ID 기본값 - 정해진 이유는 없음
export const DEFAULT_PROBLEM_ID = 56;

// n번째 스윙에 들어갈 기본 값 n에 대해 나타냄 - 정해진 이유는 없음
export const DEFAULT_SWING_INDEX = 1;

// !: 수치가 없으면 차트에 표현되지 않아야 한다.

// 데일리 차트 데이터의 count 기본값
export const DEFAULT_DAILY_ANALYSIS_COUNT_DATA = {
  count: 0.0,
};

// 데일리 차트 데이터의 최소, 최대, 평균 기본값
export const DEFAULT_DAILY_CHART_ANALYSIS_SCORE_DATA = {
  avg: 0,
  max: 0,
  min: 0,
};

// 데일리 차트 데이터의 문제점 기본값
export const DEFAULT_DAILY_CHART_PROBLEM_DATA = {
  notBad: 0.0,
  wrong: 0.0,
};

export const ENTITLEMENT: {
  [key: string]: {
    [key: string]: { [key: string]: GfProductId };
  };
} = {
  ADV: {
    V1: {
      // 24.03.21 기준 0번째(order:0)
      "1Y": "adv_v1_1y",
      // 24.03.21 기준 1번째(order:1)
      "1M": "adv_v1_1m",
      // 24.03.21 기준 테스트 요금제는 순서 무관
    },
  },
};

// 24.03.21 기준
// 요금제 나열 순서. 상품 id의 토큰 순서가 출처마다 달라 id를 키로 쓸 수 없으므로
// resolveGfPlanPeriod로 뽑은 결제 주기를 키로 삼는다.
// 주기가 없는 요금제(test_v1 등)는 순서가 무관하므로 호출부 폴백에 맡긴다.
export const GF_PLAN_PERIOD_ORDER: Record<GfPlanPeriod, number> = {
  "1y": 1, // 연 결제가 1번째
  "1m": 2, // 월 결제가 2번째
};

// 앱에서 웹으로 전달하는 요금제 목록 기본값
export const DEFAULT_GF_PURCHASE_DETAILS: GFPurchaseDetails = {
  currency: {
    // 통화 필수로 포함, e.g. KRW(ISO 4217 코드)
    code: "KRW",
  },

  // 요금제 목록 (required)
  products: [
    {
      // 구독 요금제와 매핑되는 id (required)
      gfProductId: ENTITLEMENT.ADV.V1["1Y"],
      // 기본 구독 가격 (number type) (required)
      basePrice: 99000,
      // Currency symbol로 포매팅한 기본 구독 가격 (required)
      baseFormattedPrice: "₩99,000",
    },
    {
      gfProductId: "adv_v1_1m",
      basePrice: 14900,
      baseFormattedPrice: "₩14,900",
    },
  ],
};

// 세그먼트 버튼 기본값은 0번 인덱스
export const DEFAULT_TAB_INDEX = 0;

// GTM 이벤트 수집용 data-gf-tracking-attribute
export const GF_TRACKING_ATTRIBUTE = {
  WV_HOME: {
    MARKETING_BANNER_CLICK: "marketing-banner-click-",
    PERSONAL_BANNER_CLICK: "personal-banner-click-",
    LESSON_VIDEOS_CLICK: "lesson-videos-click-",
    GOLF_VIDEOS_CLICK: "golf-videos-click-",
    USER_GUIDES_CLICK: "user-guides-click-",
    TODAY_GOAL: {
      MISSION_CARD: {
        ANALYSIS: "home-todaygoal-analysis",
        POSTURE: "home-todaygoal-posture",
        RHYTHM_TEMPO: "home-todaygoal-rhythmtempo",
      },
    },
    HOME_COMMUNITY_HOT_POST_ID: "home-community-hot-post",
    HOME_COMMUNITY_NEW_POST_ID: "home-community-new-post",
    HOME_COMMUNITY_CTA_BUTTON_ID: "home-community-cta-button",
  },
  AI_REPORT: {
    SWING_INFO_BUTTON: "swing-Info-btn",
    SEGMENTED_BUTTON: {
      MY_SWING_TAB: "my-swing-tab",
      PRO_SWING_TAB: "pro-swing-tab",
    },
    MAIN_ISSUE: {
      VIEW_MORE: "mainissue-viewmore-btn",
    },
    PROMOTION: {
      CTA_BUTTON: "app-download-cta-btn",
    },
    CTA_BUTTON: "app-download-cta-btn",
  },
  PURCHASE: {
    2: {
      SAMPLE_MONTHLY_REPORT: {
        IMPROVE_PROBLEM: {
          VIDEO_PLAY: "issue-video-play",
          TOGGLE_CLICK: "improvement-toggle-click",
        },
        SWING_TEMPO: {
          TOGGLE_CLICK: "rhymtmpo-toggle-click",
        },
      },
    },
    3: {
      EVENT: {
        1: "purchase-cta-btn",
        2: "subscription-cancel-link-google",
        3: "subscription-cancel-link-apple",
      },
    },
    NEW_USER: {
      NEW_USER_PURCHASE_CTA_BTN: "new-user-purchase-cta-btn",
      NEWBIE_PAYWALL_COACH_INFO_BTN: "newbie-paywall-coach-info-btn",
      NEWBIE_PAYWALL_FREE_START_BTN: "newbie-paywall-free-start-btn",
    },
    CHANGE: {
      FLOATING_CTA_BUTTON: "change-subscription-button",
    },
    // 신규/기존 유저 구매 페이지에서 공통으로 사용 (GTM 등록값, 변경 금지)
    BENEFIT_COMPARISON_MORE_BTN: "benefit-comparison-more-btn",
    // 혜택 비교 페이지(/purchase/benefits) — 뒤에 섹션·행 trackingName을 붙인다
    BENEFIT_COMPARISON: {
      TAB: "plan-compare-tab-",
      INFO_BUTTON: "plan-compare-info-btn-",
    },
  },
  BASIC_CHECK: {
    BASIC_CHECK_PAGE_VISIT: "view_fundamentals_contents",
    BASIC_CHECK_COMPLETE_BUTTON: "fundamentals-finish-cta",
  },
};

export const ERROR_MESSAGE = {
  UNKNOWN: "알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도하세요.",
  INVALID_DATETIME: "Invalid DateTime",
};

// 구매 상세 정보 헤더 이름
export const X_GF_PURCHASE_DETAILS_HEADER = "x-gf-purchase-details";

// 트래킹 파라미터 헤더 이름
export const X_GF_TRACKING_PARAMS_HEADER = "x-gf-tracking-params";
